import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

interface PaginationProps {
  /** Не нужен: цвета из токенов. Оставлен для совместимости. */
  isDark?: boolean;
  currentPage: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  /** «На странице»: если передан — показывается выбор размера страницы. */
  onItemsPerPageChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

/** Номера страниц с многоточиями: 1 2 3 … 205. */
function pageList(current: number, total: number): Array<number | 'gap'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('gap');
    out.push(p);
  });
  return out;
}

const squareBtn =
  'grid size-[34px] place-items-center rounded-[10px] border border-border bg-surface text-text outline-none transition-colors hover:bg-surface-alt focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:text-text-3';

/** Пагинация списка по макету «Пользователи». */
export function Pagination({
  currentPage,
  totalPages,
  total,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [10, 25, 50, 100],
  className,
}: PaginationProps) {
  const showingFrom = total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, total);
  const pages = pageList(currentPage, Math.max(1, totalPages));

  return (
    <nav
      aria-label="Страницы"
      className={cn('flex flex-wrap items-center justify-between gap-3 border-t border-border-soft px-[18px] py-3', className)}
    >
      <div className="flex items-center gap-2.5 text-[13px] text-text-2">
        {onItemsPerPageChange && (
          <label className="flex items-center gap-2.5">
            На странице
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="h-[34px] rounded-[10px] border border-border bg-surface px-2 text-sm text-text outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}
        <span className="tabular-nums">
          {fmt(showingFrom)}–{fmt(showingTo)} из {fmt(total)}
        </span>
      </div>

      <div className="flex items-center gap-1 tabular-nums">
        <button
          type="button"
          aria-label="Предыдущая страница"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className={squareBtn}
        >
          <ChevronLeft className="size-4" />
        </button>
        {pages.map((p, i) =>
          p === 'gap' ? (
            <span key={`gap-${i}`} className="px-1.5 text-text-2" aria-hidden>…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? 'page' : undefined}
              aria-label={`Страница ${p}`}
              className={cn(
                'h-[34px] min-w-[34px] rounded-[10px] px-1.5 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                p === currentPage ? 'bg-inverse font-bold text-on-inverse' : 'text-text hover:bg-muted',
              )}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          aria-label="Следующая страница"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className={squareBtn}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </nav>
  );
}
